import Component from '@ember/component';
import { min } from '@ember/object/computed';
import type RouterService from '@ember/routing/router-service';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import { serializeAppliedFilters } from 'donor-elf-web/lib/filter-utils-new';
import type FormatAmountService from 'donor-elf-web/services/format-amount-service';
import Highcharts from 'highcharts';
import { modifier } from 'ember-modifier';
import { tracked } from '@glimmer/tracking';
import lifecycle from 'donor-elf-web/modifiers/lifecycle';

interface ComponentArgs {
  months: any[];
  includeExpenses: boolean;
  includeBalance: boolean;
  transitionTo: (route: string, options?: { queryParams?: object }) => void;
}

export default class MonthlySummaryChartComponent extends Component<ComponentArgs> {
  @service declare formatAmountService: FormatAmountService;
  @service declare router: RouterService;

  @tracked months: any[];
  @tracked includeExpenses: boolean;
  @tracked includeBalance: boolean;
  @tracked transitionTo: (route: string, options?: { queryParams?: object }) => void;

  get incomeSeriesData() {
    return this.months.map((item) => {
      return {
        y: item.income,
        item: item,
      };
    });
  }

  get expenseSeriesData() {
    return this.months.map((item) => {
      return {
        y: item.expense * -1,
        item: item,
      };
    });
  }

  get balanceSeriesData() {
    return this.months.map((item) => {
      return {
        y: item.balance,
        item: item,
      };
    });
  }

  get minBalance() {
    return min('balanceSeriesData');
  }

  get categories() {
    return this.months.map((item) => {
      return item.label;
    });
  }

  get chartSettings() {
    const self = this;
    let minYAxis = undefined;

    if (!this.includeBalance || this.minBalance > 0) {
      minYAxis = 0;
    }

    const series = [
      {
        data: this.incomeSeriesData,
        name: 'Income',
        color: '#5cb85c',
        events: {
          click(event) {
            self.router.transitionTo('funds.fund.transactions', {
              queryParams: {
                filters: serializeAppliedFilters([
                  {
                    propertyName: 'transDate',
                    operation: '><',
                    val: [event.point.item.from, event.point.item.to],
                  },
                  { propertyName: 'income' },
                ]),
              },
            });
          },
        },
      },
    ];

    if (this.includeExpenses) {
      series.push({
        data: this.expenseSeriesData,
        name: 'Expenses',
        color: '#d9534f',
        events: {
          click(event) {
            self.transitionTo('funds.fund.transactions', {
              queryParams: {
                filters: serializeAppliedFilters([
                  {
                    propertyName: 'transDate',
                    operation: '><',
                    val: [event.point.item.from, event.point.item.to],
                  },
                  { propertyName: 'expense' },
                ]),
              },
            });
          },
        },
      });
    }

    if (this.includeBalance) {
      series.push({
        data: this.balanceSeriesData,
        name: 'Balance',
        color: '#0b68a1',
        type: 'line',
      });
    }

    return {
      chart: {
        type: 'column',
        height: 300,
      },
      title: null,
      credits: {
        enabled: false,
      },
      xAxis: {
        categories: this.categories,
        tickInterval: 3,
        tickLength: 0,
        labels: {
          autoRotation: false,
          align: 'left',
          y: 13,
        },
      },
      yAxis: {
        min: minYAxis,
        maxPadding: 0.0,
        endOnTick: false,
        title: {
          text: null,
        },
      },
      legend: {
        enabled: false,
      },
      tooltip: {
        formatter: function () {
          return `${this.x} ${
            this.series.name
          }: <b>${self.formatAmountService.format(this.y)}</b>`;
        },
      },
      plotOptions: {
        column: {
          groupPadding: 0.2,
          pointPadding: 0.01,
        },
      },
      series,
    } as Highcharts.Options;
  }

  renderChart = (element: HTMLCanvasElement) => {
    if (element && this.months) {
      Highcharts.chart(element, this.chartSettings);
    }
  };

  <template>
    <div {{lifecycle this.renderChart}}></div>
  </template>
}
